import React, { useState, useEffect } from "react";
import { X, Check, Edit3, Sparkles, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  placeholders: string[];
  isOpen: boolean;
  onClose: () => void;
  onApply: (replacements: Record<string, string>) => void;
}

export const PlaceholderFillerModal: React.FC<Props> = ({
  placeholders,
  isOpen,
  onClose,
  onApply,
}) => {
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      const initial: Record<string, string> = {};
      placeholders.forEach((p) => {
        initial[p] = formValues[p] || "";
      });
      setFormValues(initial);
    }
  }, [isOpen, placeholders]);

  if (!isOpen) return null;

  const handleChange = (key: string, val: string) => {
    setFormValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    onApply(formValues);
    onClose();
  };

  const filledCount = Object.values(formValues).filter((v) => v && v.trim()).length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-amber-50/70 via-indigo-50/40 to-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600 flex-shrink-0">
              <Edit3 className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 tracking-tight">
                Completar Campos del Documento
              </h3>
              <p className="text-xs text-slate-500">
                Sustitución guiada de variables y datos faltantes entre corchetes
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleApply} className="flex flex-col flex-1 overflow-hidden">
          <div className="p-6 overflow-y-auto flex flex-col gap-4 flex-1">
            {placeholders.length === 0 ? (
              <div className="text-center py-12 px-4 text-slate-500">
                <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-800">
                  ¡No se detectaron campos pendientes!
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  El documento redactado contiene toda la información fáctica procesada.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-xs text-slate-600">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span>
                      Detectados <strong className="text-slate-900">{placeholders.length}</strong> campos pendientes
                    </span>
                  </div>
                  <span className="font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200/50">
                    {filledCount} de {placeholders.length} completados
                  </span>
                </div>

                <div className="flex flex-col gap-3">
                  {placeholders.map((pKey, idx) => {
                    const formattedLabel = pKey
                      .toLowerCase()
                      .split(/[\s_]+/)
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(" ");

                    return (
                      <div
                        key={idx}
                        className="flex flex-col gap-1.5 bg-slate-50/60 hover:bg-slate-50 p-3 rounded-xl border border-slate-200 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <label className="text-xs font-semibold text-slate-800">
                            {formattedLabel}
                          </label>
                          <span className="text-[11px] font-mono text-amber-700 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 max-w-[240px] truncate">
                            [{pKey}]
                          </span>
                        </div>
                        {pKey.length > 40 || /[\n\r]|describir|hecho|motivo|razon|pretension|servicio|observacion|detalle|clausula|direccion|explic/i.test(pKey) ? (
                          <Textarea
                            rows={3}
                            value={formValues[pKey] || ""}
                            onChange={(e) => handleChange(pKey, e.target.value)}
                            placeholder={`Describa detalladamente para [${pKey}]...`}
                            className="min-h-[75px] text-xs bg-white border-slate-200 focus-visible:ring-indigo-500 resize-y"
                          />
                        ) : (
                          <Input
                            type="text"
                            value={formValues[pKey] || ""}
                            onChange={(e) => handleChange(pKey, e.target.value)}
                            placeholder={`Ingrese valor para [${pKey}]...`}
                            className="h-9 text-xs bg-white border-slate-200 focus-visible:ring-indigo-500"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="text-xs font-semibold text-slate-600 hover:bg-slate-100"
            >
              Cancelar
            </Button>
            <div className="flex items-center gap-2">
              <Button
                type="submit"
                disabled={placeholders.length === 0 || filledCount === 0}
                className="gap-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-md active:scale-95"
              >
                <Check className="w-4 h-4" />
                Aplicar al Documento ({filledCount})
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
